import { API } from "aws-amplify";

const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';
const api_name = isCorporateMode 
    ? "Corporate-API"
    : (process.env.REACT_APP_STAGE === "DEV" ? "Node-API" : "prod-api");

// Add Corporate User
export const addCorporateUser = async (inputs) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "AdminCreateUser",  // Use AdminCreateUser to create Cognito user and send email
                fname: inputs.first_name,
                lname: inputs.last_name,
                email: inputs.email,
                phone: inputs.phone || "",
                dob: inputs.dob || "",
                qualification: inputs.qualification || "",
                role: inputs.role || "Employee",
                batch: inputs.batch || "",
                graduationYear: inputs.graduationYear || "",
                course: inputs.course || "",
                status: inputs.profile_status || "Active",
                designation: inputs.job_title || "",
                department: inputs.department || "",
                employee_id: inputs.employee_id || "",
                date_of_joining: inputs.date_of_joining || "",
                internal_experience: inputs.internal_experience || "",
                reporting_l1: inputs.reporting_l1 || "",
                reporting_l2: inputs.reporting_l2 || "",
                work_location: inputs.work_location || "",
                company: inputs.company || "",
                notes: inputs.notes || "",
                photo: inputs.photo || "",
                program_code: Array.isArray(inputs.program_code) 
                    ? inputs.program_code.join(',') 
                    : (inputs.program_code || ""),
                created_by: inputs.created_by,
                updated_by: inputs.updated_by || inputs.created_by,
                created_date: new Date().toISOString(),
                updated_date: new Date().toISOString(),
                userType: "corporate",  // Flag to indicate this is a corporate user
                isCorporate: true
            },
            response: true,
            convertEmptyValues: true,
        };

        const response = await API.post(apiName, path, params);
        return response;
    } catch (error) {
        console.error("Error adding corporate user:", error);
        throw error;
    }
};

// Get Corporate User by Email
export const getCorporateUser = async (email) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "getCorporateUser",
                email: email,
            },
            response: true,
            convertEmptyValues: true,
            // no explicit auth headers
        };

        const response = await API.post(apiName, path, params);
        if (response.status === 200 && response.data) {
            // Some backends double-encode body. Parse defensively.
            let body = response.data?.body ?? response.data;
            try { if (typeof body === 'string') body = JSON.parse(body); } catch {}
            try { if (body && typeof body.body === 'string') body = JSON.parse(body.body); } catch {}
            return body;
        }
        return null;
    } catch (error) {
        console.error("Error getting corporate user:", error);
        throw error;
    }
};

// Get All Corporate Users
export const getAllCorporateUsers = async () => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "getAllCorporateUsers",
            },
            response: true,
            convertEmptyValues: true,
            // no explicit auth headers
        };

        const response = await API.post(apiName, path, params);
        // Parse defensively to handle double-encoded bodies and irregular status shapes
        let body = response?.data?.body ?? response?.data ?? response;
        try { if (typeof body === 'string') body = JSON.parse(body); } catch {}
        try { if (body && typeof body.body === 'string') body = JSON.parse(body.body); } catch {}
        if (body && body.body && typeof body.body === 'object') body = body.body;
        console.log("Fetched corporate users response body:", response, body);
        // Identify array of users across possible shapes
        const usersArr = Array.isArray(body?.users) ? body.users
            : Array.isArray(body?.Items) ? body.Items
            : Array.isArray(body?.body?.users) ? body.body.users
            : Array.isArray(body?.body?.Items) ? body.body.Items
            : Array.isArray(body) ? body
            : [];

        const val = (obj, key) => {
            const v = obj?.[key];
            if (v && typeof v === 'object' && 'S' in v) return v.S || '';
            return v || '';
        };
        console.log("Fetched users array:", usersArr);
        const resultData = usersArr.map((item, index) => {
            // Support DynamoDB attribute map or plain JSON
            const email = val(item, 'email');
            const first_name = val(item, 'first_name');
            const last_name = val(item, 'last_name');
            const job_title = val(item, 'job_title');
            const department = val(item, 'department');
            const role = val(item, 'role') || 'Employee';
            const employee_id = val(item, 'employee_id');
            const date_of_joining = val(item, 'date_of_joining');
            const internal_experience = val(item, 'internal_experience');
            const reporting_l1 = val(item, 'reporting_l1');
            const reporting_l2 = val(item, 'reporting_l2');
            const work_location = val(item, 'work_location');
            const company = val(item, 'company');
            const notes = val(item, 'notes');
            const profile_status = val(item, 'profile_status') || 'Active';
            const program_code = val(item, 'program_code');
            const created_by = val(item, 'created_by');
            const updated_by = val(item, 'updated_by');
            const created_date = val(item, 'created_date');
            const updated_date = val(item, 'updated_date');
            const photo = val(item, 'photo');

            return {
                id: index + 1,
                email,
                first_name,
                last_name,
                name: `${first_name || ''} ${last_name || ''}`.trim(),
                photo,
                job_title,
                department,
                role,
                employee_id,
                date_of_joining,
                internal_experience,
                reporting_l1,
                reporting_l2,
                work_location,
                company,
                notes,
                profile_status,
                status: profile_status,
                program_code,
                created_by,
                updated_by,
                created_date,
                updated_date,
            };
        });

        return Array.isArray(resultData) ? resultData : [];
    } catch (error) {
        console.error("Error getting all corporate users:", error);
        return [];
    }
};

// Get Corporate Users by Program Code
export const getCorporateUsersByProgramCode = async (program_code) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "getCorporateUsersByProgramCode",
                program_code: program_code,
            },
            response: true,
            convertEmptyValues: true,
            // no explicit auth headers
        };

        const response = await API.post(apiName, path, params);
        // Parse defensively to handle double-encoded bodies and irregular status shapes
        let body = response?.data?.body ?? response?.data ?? response;
        try { if (typeof body === 'string') body = JSON.parse(body); } catch {}
        try { if (body && typeof body.body === 'string') body = JSON.parse(body.body); } catch {}
        if (body && body.body && typeof body.body === 'object') body = body.body;

        const usersArr = Array.isArray(body?.users) ? body.users
            : Array.isArray(body?.Items) ? body.Items
            : Array.isArray(body?.body?.users) ? body.body.users
            : Array.isArray(body?.body?.Items) ? body.body.Items
            : Array.isArray(body) ? body
            : [];

        const val = (obj, key) => {
            const v = obj?.[key];
            if (v && typeof v === 'object' && 'S' in v) return v.S || '';
            return v || '';
        };

        const resultData = usersArr.map((item, index) => ({
            id: index + 1,
            email: val(item, 'email'),
            first_name: val(item, 'first_name'),
            last_name: val(item, 'last_name'),
            name: `${val(item, 'first_name') || ''} ${val(item, 'last_name') || ''}`.trim(),
            photo: val(item, 'photo'),
            job_title: val(item, 'job_title'),
            department: val(item, 'department'),
            role: val(item, 'role') || 'Employee',
            employee_id: val(item, 'employee_id'),
            date_of_joining: val(item, 'date_of_joining'),
            internal_experience: val(item, 'internal_experience'),
            reporting_l1: val(item, 'reporting_l1'),
            reporting_l2: val(item, 'reporting_l2'),
            work_location: val(item, 'work_location'),
            company: val(item, 'company'),
            notes: val(item, 'notes'),
            profile_status: val(item, 'profile_status') || 'Active',
            status: val(item, 'profile_status') || 'Active',
            program_code: val(item, 'program_code'),
            created_by: val(item, 'created_by'),
            updated_by: val(item, 'updated_by'),
            created_date: val(item, 'created_date'),
            updated_date: val(item, 'updated_date'),
        }));

        return Array.isArray(resultData) ? resultData : [];
    } catch (error) {
        console.error("Error getting corporate users by program code:", error);
        return [];
    }
};

// Update Corporate User
export const updateCorporateUser = async (inputs) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "updateCorporateUser",
                email: inputs.email,
                first_name: inputs.first_name,
                last_name: inputs.last_name,
                photo: inputs.photo || "",
                job_title: inputs.job_title || "",
                department: inputs.department || "",
                role: inputs.role || "Employee",
                employee_id: inputs.employee_id || "",
                date_of_joining: inputs.date_of_joining || "",
                internal_experience: inputs.internal_experience || "",
                reporting_l1: inputs.reporting_l1 || "",
                reporting_l2: inputs.reporting_l2 || "",
                work_location: inputs.work_location || "",
                company: inputs.company || "",
                notes: inputs.notes || "",
                profile_status: inputs.profile_status || "Active",
                program_code: Array.isArray(inputs.program_code) 
                    ? inputs.program_code.join(',') 
                    : (inputs.program_code || ""),
                updated_by: inputs.updated_by,
                updated_date: new Date().toISOString(),
            },
            response: true,
            convertEmptyValues: true,
            // no explicit auth headers
        };

        const response = await API.post(apiName, path, params);
        return response;
    } catch (error) {
        console.error("Error updating corporate user:", error);
        throw error;
    }
};

// Update Corporate User Photo
export const updateCorporateUserPhoto = async (email, photo, updated_by) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "updateCorporateUserPhoto",
                email: email,
                photo: photo,
                updated_by: updated_by,
                updated_date: new Date().toISOString(),
            },
            response: true,
            convertEmptyValues: true,
            // no explicit auth headers
        };

        const response = await API.post(apiName, path, params);
        return response;
    } catch (error) {
        console.error("Error updating corporate user photo:", error);
        throw error;
    }
};

// Update Corporate User Notes
export const updateCorporateUserNotes = async (email, notes, updated_by) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "updateCorporateUserNotes",
                email: email,
                notes: notes,
                updated_by: updated_by,
                updated_date: new Date().toISOString(),
            },
            response: true,
            convertEmptyValues: true,
            // no explicit auth headers
        };

        const response = await API.post(apiName, path, params);
        return response;
    } catch (error) {
        console.error("Error updating corporate user notes:", error);
        throw error;
    }
};

// Update Corporate User Status
export const updateCorporateUserStatus = async (email, profile_status, updated_by) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "updateCorporateUserStatus",
                email: email,
                profile_status: profile_status,
                updated_by: updated_by,
                updated_date: new Date().toISOString(),
            },
            response: true,
            convertEmptyValues: true,
            headers: {},
        };

        const response = await API.post(apiName, path, params);
        return response;
    } catch (error) {
        console.error("Error updating corporate user status:", error);
        throw error;
    }
};

// Delete Corporate User
export const deleteCorporateUser = async (email) => {
    try {
        let apiName = api_name;
        let path = "";
        let params = {
            body: {
                command: "deleteCorporateUser",
                email: email,
            },
            response: true,
            convertEmptyValues: true,
            headers: {},
        };

        const response = await API.post(apiName, path, params);
        return response;
    } catch (error) {
        console.error("Error deleting corporate user:", error);
        return { data: { statusCode: 500, body: JSON.stringify({ message: "Error deleting corporate user" }) } };
    }
};
